import styles from './index.module.css'

import { IoArrowUp } from '@react-icons/all-files/io5/IoArrowUp'
import { IoClose } from '@react-icons/all-files/io5/IoClose'
import { SiApplemusic } from '@react-icons/all-files/si/SiApplemusic'
import { SiSpotify } from '@react-icons/all-files/si/SiSpotify'
import { SiYoutube } from '@react-icons/all-files/si/SiYoutube'
import React, { useEffect, useRef, useState } from 'react'

import DiscoIcon from 'assets/icons/disco-bolt.svg'

import { Box, Button, Flex, IconButton, Text, TextArea } from 'components/ds'

type Props = {
  value: string
  onChange: (value: string) => void
  onClear: () => void
  onSubmit: () => void
}

export const AI_PROMPT_SUGGESTIONS = [
  'Upbeat tracks with female vocals for a workout playlist',
  'Cinematic orchestral pieces for dramatic scenes',
  'Lo-fi hip hop with a melancholic late-night mood',
]

const OVERLAY_ANIMATION_DURATION = 2400

const AiSearchOverlay = ({
  value,
  onChange,
  onClear,
  onSubmit,
}: Props): React.ReactNode => {
  const [rainbowDone, setRainbowDone] = useState(false)
  const hasText = value.trim().length > 0

  // Move caret to end on first focus, but not on subsequent focuses
  const caretMovedRef = useRef(false)
  const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    if (caretMovedRef.current) return
    caretMovedRef.current = true
    const end = e.currentTarget.value.length
    e.currentTarget.setSelectionRange(end, end)
  }

  // Use timeout instead of onAnimationEnd — CSS Modules hash keyframe names
  useEffect(() => {
    const timer = setTimeout(
      () => setRainbowDone(true),
      OVERLAY_ANIMATION_DURATION,
    )
    return () => clearTimeout(timer)
  }, [])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      // Callers decide what an empty submit means; Browse clears its search
      onSubmit()
    }
  }

  return (
    <Box className={styles.overlay}>
      <Flex direction="column" gap="3" className={styles.expanded}>
        <Box
          className={`${styles.inputWrapper} ${
            rainbowDone ? styles.inputWrapperDone : ''
          }`}
        >
          <Box className={styles.inputInner}>
            {hasText && (
              <IconButton
                variant="ghost"
                size="1"
                radius="full"
                className={styles.closeBtn}
                onClick={onClear}
                aria-label="Clear prompt"
              >
                <IoClose size={16} />
              </IconButton>
            )}
            <TextArea
              className={styles.textarea}
              value={value}
              onChange={e => onChange(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={handleFocus}
              placeholder='AI Search works best when you describe the feel or scene. Try adding mood, tempo or a reference. For example, "I need music that feels like the Stranger Things opening credits: slow, pulsing, ominous."'
              aria-label="AI search prompt"
              rows={3}
              grow
              autoFocus
            />
            <IconButton
              variant="solid"
              size="2"
              radius="full"
              className={styles.sendBtn}
              disabled={!hasText}
              onClick={onSubmit}
              aria-label="Send"
            >
              <IoArrowUp size={16} />
            </IconButton>
          </Box>
        </Box>

        {!hasText && (
          <Flex wrap="wrap" gap="2" className={styles.suggestions}>
            {AI_PROMPT_SUGGESTIONS.map(s => (
              <Button
                key={s}
                variant="soft"
                size="1"
                className={styles.suggestionChip}
                onClick={() => onChange(s)}
              >
                <Text size="1">{s}</Text>
              </Button>
            ))}
          </Flex>
        )}

        {!hasText && (
          <Flex align="center" gap="2" className={styles.linkRow}>
            <Text size="2" color="gray">
              You can also paste a link from
            </Text>
            <DiscoIcon width={16} height={16} className={styles.logoDisco} />
            <SiSpotify size={16} className={styles.logoSpotify} />
            <SiYoutube size={16} className={styles.logoYoutube} />
            <SiApplemusic size={16} className={styles.logoApple} />
          </Flex>
        )}
      </Flex>
    </Box>
  )
}

export default AiSearchOverlay
